<template>
  <div class="activity-stream">
    <!-- skeleton -->
    <template v-if="loading">
      <skeleton-activity-stream class="activity-stream-wrapper" />
      <skeleton-activity-stream class="activity-stream-wrapper" />
      <skeleton-activity-stream class="activity-stream-wrapper" />
      <skeleton-activity-stream class="activity-stream-wrapper" />
    </template>
    <!-- el -->
    <template v-else>
      <div 
        class="activity-stream-wrapper no-result"
        v-if="!viewData || viewData && viewData.length === 0">
        No result.
      </div>
      <div 
        class="activity-stream-wrapper" 
        v-for="(x, ind) in viewData" 
        :key="`${widget.fieldName}-${ind}-${x.id}`"
        @click="open(x)"
        v-else>
        <fts-profile 
          :profilePhotoSize="50"
          :userName="x.audit && x.audit.updatedBy"
          profileNameClass="activity-stream-info"
          class="activity-stream__header">
          <template #profileName="{userProfileName}">
            <!-- alpha -->
            <div class="activity-stream-row-alpha">
              <div class="activity-stream-row-alpha-l">
                <span class="profile-name">{{userProfileName}}</span> 
                <template v-if="x.activityName">
                  has done <span class="activity-name">{{x.activityName}}</span> on 
                </template>
                <span v-else-if="x.historyType === 1">created</span>
                <span v-else-if="x.historyType === 2">updated</span>
                <span v-else-if="x.historyType === 4">change state on</span>  
                <span v-else-if="x.historyType === 7">has done assigned on</span>
                <span v-else-if="x.historyType === 8">imported</span>
                <span v-else-if="x.historyType === 9">merged</span>
                <span v-else>commented on</span>
                <span class="title-name">{{x.title}}</span>
              </div>
              <fts-state :state="x.toState" :states="states" class="d-state" v-if="x.toState"/>
            </div>
            <!-- beta -->
            <div class="activity-stream-row-beta">
              <div class="activity-stream-row-beta-l">
                <div class="d-document-id">{{x.document && x.document.documentId}}</div>
                <fts-label-group
                  :item-id="x.vectorId"
                  :list="workspaceModules"
                  emoji
                  :emoji-size="12"
                  class="d-module" 
                  v-if="x.vectorId"
                />
              </div>
              <!-- audit created -->
              <div class="d-audit audit-created" v-if="x.audit && x.audit.createdBy && x.historyType !== 1">
                <div class="d-audit-by">by</div>
                <fts-profile :userName="x.audit.createdBy" class="d-audit-profile" />
                <fts-tippy class="d-audit-date" :content="moment(x.audit.createdDate).format('llll')">
                  <template #trigger>{{dateFormat(x.audit.createdDate)}}</template>
                </fts-tippy>
              </div> 
            </div>
            <!-- gamma -->
            <div class="activity-stream-row-gamma">
              <!-- collaboration -->
              <div 
                class="d-collaboration fts-collaboration" 
                v-if="x.collaborationFlags && [1, 2, 5, 10].indexOf(x.collaborationFlags) > -1">
                <div class="collaboration-wrapper" v-if="x.collaborationFlags === 2 || x.collaborationFlags === 10">
                  <div 
                    class="collaboration-trigger collaboration-intention"
                    :class="{ 'collaboration-last': x.collaborationFlags === 10}">
                    <i class="fts-icon-exclamation-triangle"></i>
                  </div>
                </div>
                <div class="collaboration-wrapper" v-if="x.collaborationFlags === 1 || x.collaborationFlags === 5">
                  <div 
                    class="collaboration-trigger collaboration-comment"
                    :class="{ 'collaboration-last': x.collaborationFlags === 5}">
                    <i class="fts-icon-comment"></i>
                  </div>
                  <div class="collaboration-count" v-if="x.commentCount">{{x.commentCount}}</div>
                </div>
              </div>
              <!-- audit created -->
              <template v-if="x.audit && x.audit.createdDate && x.historyType === 1">
                <fts-tippy class="d-audit-date" :content="moment(x.audit.createdDate).format('llll')">
                  <template #trigger>{{dateFormat(x.audit.createdDate)}}</template>
                </fts-tippy>
              </template>
              <!-- audit updated -->
              <template v-else-if="x.audit && x.audit.updatedBy">
                <fts-tippy class="d-audit-date" :content="moment(x.audit.updatedDate).format('llll')">
                  <template #trigger>{{dateFormat(x.audit.updatedDate)}}</template>
                </fts-tippy>
              </template>
            </div>
          </template>
        </fts-profile>
        <!-- content -->
        <div class="activity-stream-content" v-html="x.content" v-if="commentContent(x.content)"></div>
        <!-- attachments -->
        <fts-file 
          :prefixURL="`${x.vectorId}-comment/`" 
          :data="x.attachments" 
          downloadable 
          class="activity-stream-attachments stopPropagation" />
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";

import commons from "../../commons/commons";
import { htmlValidator } from "../../commons/validator";
import { clientStore } from '../../store/clientStore';
import { store as canvasDesignStore } from "../../store/canvasDesignStore";

import FTSTippy from "../../elements/FTSTippy";
import FTSProfile from "../../elements/FTSProfile";
import FTSState from "../../elements/FTSState";
import FTSFile from "../../elements/file/FTSFile";
import FTSLabelGroup from "../../elements/FTSLabelGroup";
import SkeletonActivityStream from "../../elements/skeleton-screen/dashboard/SkeletonActivityStream";

export default {
  props: {
    widget: {
      type: Object,
      default: () => ({}),
    },
    viewData: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
  },
  components: {
    "fts-tippy": FTSTippy,
    "fts-profile": FTSProfile,
    "fts-state": FTSState,
    "fts-file": FTSFile,
    "fts-label-group": FTSLabelGroup,
    "skeleton-activity-stream": SkeletonActivityStream,
  },
  methods: {
    moment,
    dateFormat(date) {
      return commons.momentRelativeTime(date)
    },
    commentContent(content) {
      return htmlValidator(content)
    },
    open(item) {
      if (commons.elementStopPropagation()) return;
      this.$emit("open", item);
    },
  },
  computed: {
    states(){
      if (this.widget && this.widget.source === 'studio')
        return canvasDesignStore.getters.workspaceStates;
    },
    workspaceModules() {
      if (
        Array.isArray(clientStore.state.workspaceModules) && 
        clientStore.state.workspaceModules.length
      )
        return clientStore.state.workspaceModules;
      else if (
        Array.isArray(canvasDesignStore.getters.workspaceModules) && 
        canvasDesignStore.getters.workspaceModules.length
      )
        return canvasDesignStore.getters.workspaceModules;
      
      return [];
    },
  },
}
</script>

<style lang="scss">
.activity-stream-wrapper {
  &:not(:first-child) {
    margin-top: 1rem;
  }
  &:not(.no-result) {
    border-radius: .575rem;
    background: var(--fts-card-background);
    color: var(--fts-text);
    box-shadow: var(--fts-elevation-3);
    cursor: pointer;
  }
  &.no-result {
    font-size: .875rem;
    color: var(--fts-gray-400);
  }
  padding: 1rem;
  .activity-stream__header {
    align-items: flex-start;
    .activity-stream-info {
      min-width: 0;
      flex: 1;
    }
    .activity-stream-row-alpha {
      min-width: 0;
      display: flex;
      align-items: flex-start;
      margin-bottom: 0.25rem;
      .activity-stream-row-alpha-l {
        flex: 1 1 70%;
        font-size: 0.875rem;
        line-height: 1.25rem;
        word-break: break-word;
        .profile-name {
          font-weight: 600;
        }
        .activity-name {
          font-weight: 500;
        }
        .title-name {
          font-weight: 500;
        }
      }
      .d-state {
        margin-left: .75rem;
      }
    }
    .activity-stream-row-beta {
      min-width: 0;
      display: flex;
      align-items: center;
      margin-bottom: .375rem;
      .activity-stream-row-beta-l {
        min-width: 0;
        flex: 1 1 60%;
        display: flex;
        align-items: center;
        .d-document-id {
          margin-right: .75rem;
        }
      }
      .d-audit {
        margin-left: .75rem;
        &.audit-created {
          .d-audit-profile {
            max-width: 60px;
          }
        }
      }
    }
    .activity-stream-row-gamma {
      min-width: 0;
      display: flex;
      align-items: center;
      .d-collaboration {
        margin-right: .75rem;
      }
    }

  }
  .activity-stream-content {
    display: inline-block;
    margin-top: .375rem;
    padding: 1rem .75rem;
    word-break: break-all;
    white-space: pre-wrap;
    font-size: 1.25rem;
  }
  .activity-stream-attachments {
    margin-top: .375rem;
  }
}
</style>

